import { getAgencyUser } from '@/app/(main)/agency/(auth)/helper'
import { ChatFlowHandler } from '../types'
import { generateImage } from './api'

interface UploadResponse {
  message: string
  destination_path: string[]
}

export interface ImageGenerationState {
  userPrompt: string
  currentQuestion: string | null
  imageGenerated: boolean
  selectedFolderName: string | null
  agencyUserId: string
  isGenerating: boolean
  handleSaveImage?: (
    folderName: string,
    imageData: string,
    agencyUserId: string,
    type: string
  ) => Promise<UploadResponse>
  showToast?: (message: {
    title: string
    description: string
    variant?: 'default' | 'destructive'
  }) => void
}

interface InitialData {
  selectedFolderName?: string
  agencyUserId?: string
  handleSaveImage?: (
    folderName: string,
    agencyUserId: string,
    type: string,
    imageData: string
  ) => Promise<UploadResponse>
  showToast?: (message: {
    title: string
    description: string
    variant?: 'default' | 'destructive'
  }) => void
}

interface GenerateImageResponse {
  status: number
  message: string
  image_data: {
    url: string
    prompt: string
    aspect_ratio: string
    output_quality: number
  }[]
  count: number
  type: string
  result?: string
}

interface PageNavigationResponse {
  message: string
  type: string
  result: string
  status: number
}

export const imageGenerationChatFlow: ChatFlowHandler = {
  getInitialMessages: () => {
    return []
  },

  getInitialState: (initialData: InitialData) => {
    return {
      userPrompt: '',
      currentQuestion:
        'The more details you provide, the better I can design your image. What should it look like?',
      imageGenerated: false,
      selectedFolderName: initialData?.selectedFolderName || 'Waltz',
      handleSaveImage: initialData?.handleSaveImage,
      showToast: initialData?.showToast,
      agencyUserId: initialData?.agencyUserId || ''
    } as ImageGenerationState
  },

  initialize: async (state: ImageGenerationState, sendMessage) => {
    if (state.currentQuestion) {
      await sendMessage(state.currentQuestion)
    }
  },

  handleUserInput: async (
    input: string,
    state: ImageGenerationState,
    sendMessage
  ) => {
    const newState = { ...state }
    newState.isGenerating = true

    try {
      newState.userPrompt = input

      await sendMessage('Analyzing your request.', {
        isGenerating: true // Pass loading state to message
      })
      const agencyUser= getAgencyUser()

      const response = (await generateImage({
        prompt: input,
        go_fast: true,
        megapixels: '1',
        num_outputs: 3,
        aspect_ratio: '1:1',
        output_format: 'png',
        output_quality: 100,
        num_inference_steps: 4,
        agency_user_id:agencyUser?.agencyUserId?.id

      })) as GenerateImageResponse | PageNavigationResponse

      newState.isGenerating = false

      if (response.type === 'navigation' && response.status === 200) {
        await sendMessage(response.message)
        return { newState, navigateTo: response.result }
      } else if (response.type === 'navigation' && response.status === 404) {
        await sendMessage(response.result || 'No route found')
        return { newState }
      }

      const imageDataResponse = response as GenerateImageResponse

      await sendMessage(
        `Do these images fit your project? Use only the ones you need, save all images in the "${state.selectedFolderName}" folder, or generate a new image if you want to explore further.`,
        {
          images: imageDataResponse?.image_data?.map(
            (image: { url: string }) => ({
              url: image.url,
              actionButton: {
                text: 'Use It',
                onClick: async () => {
                  try {
                    if (!state.handleSaveImage) {
                      throw new Error('Upload function not available')
                    }

                    const uploadResponse = await state.handleSaveImage(
                      state.selectedFolderName || 'Waltz',
                      state.agencyUserId || '',
                      'url',
                      image.url
                    )

                    if (state.showToast && uploadResponse) {
                      state.showToast({
                        title: 'Success',
                        description: 'Image saved successfully!',
                        variant: 'default'
                      })
                    }
                  } catch (error) {
                    console.error('Error saving image:', error)
                    if (state.showToast) {
                      state.showToast({
                        title: 'Error',
                        description: 'Failed to save image. Please try again.',
                        variant: 'destructive'
                      })
                    }
                  }
                }
              }
            })
          )
        }
      )

      await sendMessage(
        'Would you like to generate another image? Just type your prompt!'
      )

      return { newState }
    } catch (error) {
      const err = error as { message?: string }
      const message = err.message || String(error)

      if (message.includes('Image generation quota exceeded')) {
        await sendMessage('I apologize, Your image generation quota exceeded.')
        return { newState }
      } else {
        await sendMessage(
          'I apologize, but I encountered an error generating your images. Please try again.'
        )
      }

      return { newState }
    }
  },

  handleVariantSelection: async () => {
    return { content: '', closeChat: false }
  }
}
